import { useTranslation } from "@onefront/react-sdk";
import { Stack } from "@vapor/react-material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Feedback } from "../../../../../../@types/enums/general/Feedback";
import { BusinessAccountingForm } from "../../../../../../components/form/business/accounting";
import WsSnackbar, { WsSnackbarTypes } from "../../../../../../components/ws-snackbar";
import { useGetBusiness } from "../../../../../../hooks/api/business/useGetBusiness";
import { usePatchAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePatchAdditionalBusinessDetails";
import { usePatchBusiness } from "../../../../../../hooks/api/business/usePatchBusiness";

export const BusinessEditAccounting = ({ isEditing, setIsEditing, setLoadingSection, loading, fetchBusiness }) => {
    const { t } = useTranslation();
    const { workspaceId, businessId } = useParams();
    const [accountingConfiguration, setAccountingConfiguration] = useState<any>({
        accountingType: "ORDINARY",
        simplifiedAccountingType: null,
        vatReportsType: null
    });
    const [asset, setAsset] = useState<any>({
        assetGroup: null,
        assetSpecies: null
    });
    const [feedback, setFeedback] = useState<Feedback | null>(null);

    const { data: business, loading: loadingGet, fetch: getBusiness } = useGetBusiness(workspaceId, businessId);

    const { loading: loadingPatchAccounting, fetch: patchBusinessAccounting } = usePatchAdditionalBusinessDetails(
        workspaceId,
        businessId,
        "accounting-configuration",
        business?.accountingConfiguration?.id
    );

    const { loading: loadingPatchBusiness, fetch: patchBusiness } = usePatchBusiness(workspaceId, businessId);

    const handleAccounting = useCallback(data => {
        setAccountingConfiguration(prev => ({
            ...prev,
            ...data
        }));
    }, []);

    const handleAssets = useCallback((val, key) => {
        setAsset(prev => ({
            ...prev,
            ...(key === "assetGroup" ? { assetSpecies: null } : {}),
            [key]: val
        }));
    }, []);

    useEffect(() => {
        if (businessId && !business && !loadingGet) {
            getBusiness?.().then(res => {
                const { asset, accountingConfiguration } = res?.data || {
                    asset: null,
                    accountingConfiguration: null
                };

                const { id, ...accountingConfigurationData } = accountingConfiguration;

                if (accountingConfiguration) {
                    setAccountingConfiguration(prev => ({
                        ...prev,
                        ...accountingConfigurationData
                    }));
                }
                if (asset) {
                    setAsset(prev => ({
                        ...prev,
                        ...asset
                    }));
                }
            });
        }
    }, [businessId, getBusiness, business, loadingGet, setIsEditing]);

    useEffect(() => {
        setLoadingSection(loadingGet);
    }, [loadingGet, setLoadingSection]);

    return (
        <Stack sx={{ position: "relative" }}>
            {feedback && (
                <WsSnackbar
                    position={"absolute"}
                    sx={{
                        right: "0 !important",
                        top: "-4.5rem !important",
                        width: 700
                    }}
                    type={feedback === Feedback.ERROR ? WsSnackbarTypes.error : WsSnackbarTypes.success}
                    title={t(`business.accounting.title`)}
                    message={t(`feedback.business.edit.${feedback}`)}
                    horizontal="right"
                    handleClose={() => setFeedback(null)}
                />
            )}
            <BusinessAccountingForm
                asset={asset}
                atecoCode={business?.atecoCode}
                selfEmployed={business?.selfEmployed}
                accountingConfiguration={accountingConfiguration}
                handleAssets={handleAssets}
                handleAccounting={handleAccounting}
                onSave={() => {
                    const id = business?.accountingConfiguration?.id;
                    patchBusinessAccounting({
                        url: `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/business/${businessId}/accounting-configuration/${id}`,
                        data: {
                            ...accountingConfiguration,
                            ...(business?.selfEmployed && accountingConfiguration?.accountingType === "ORDINARY"
                                ? { simplifiedAccountingType: "CASH_ACCOUNTING" }
                                : {})
                        }
                    })
                        .then(res => {
                            patchBusiness({
                                data: { asset: { ...asset } }
                            })
                                .then(res => {
                                    setFeedback(Feedback.SUCCESS);
                                    setIsEditing(false);
                                    getBusiness?.();
                                })
                                .catch(err => {
                                    setFeedback(Feedback.ERROR);
                                })
                                .then(() => {
                                    fetchBusiness();
                                });
                        })
                        .catch(err => {
                            setFeedback(Feedback.ERROR);
                        });
                }}
                onCancel={() => {
                    const { asset, accountingConfiguration } = business || {
                        asset: null,
                        accountingConfiguration: null
                    };

                    const { id, ...accountingConfigurationData } = accountingConfiguration;

                    setAccountingConfiguration(accountingConfigurationData);
                    setAsset(asset);
                    setIsEditing(false);
                }}
                isEditing={isEditing}
                isNewBusiness={false}
                loading={loading || loadingPatchAccounting || loadingPatchBusiness || loadingGet}
            />
        </Stack>
    );
};
